import { hasAdminRole } from '@/hooks/use-is-admin'
import type { PartnerProfile } from '@/store'

/**
 * Single source of truth for "which gate, if any, blocks this partner from the
 * portal". Both route guards (`_private`, `_auth`) and the gate screens read
 * this — never re-derive the decision inline, or the lockstep with the backend
 * drifts. The backend's `IsAffiliatePartner` gates on `can_access_portal`, NOT
 * `status`, and lets MANAGER/ADMIN bypass both; this mirrors that admin bypass.
 *
 * Order matters: beta access is checked before T&C, because a partner who cannot
 * access the portal at all should never be asked to accept terms for it.
 */
export type PortalGate = { kind: 'allow' } | { kind: 'beta-locked' } | { kind: 'terms' }

/** A gate that actually redirects somewhere — the `allow` gate has no path. */
type BlockingGate = Exclude<PortalGate, { kind: 'allow' }>

const assertNever = (value: never): never => {
  throw new Error(`Unhandled PortalGate kind: ${JSON.stringify(value)}`)
}

/**
 * Redirect path for each blocking gate. A `switch` (not a lookup map) so that
 * adding a `PortalGate` variant without a path fails the type-check here — the
 * "add a variant → add a path" contract is enforced by the compiler, not by a
 * runtime `undefined` slipping into `redirect({ to })`.
 */
export const PORTAL_GATE_PATH = (gate: BlockingGate): string => {
  switch (gate.kind) {
    case 'beta-locked':
      return '/beta-locked'
    case 'terms':
      return '/accept-terms'
    default:
      return assertNever(gate)
  }
}

export const getPortalGate = (partner: PartnerProfile | null): PortalGate => {
  if (partner === null) {
    // Fail-open is safe ONLY because every caller gates on `isAuthorized` first
    // (see `_private.tsx`, `_auth.tsx`, `accept-terms.tsx`, `beta-locked.tsx`),
    // and the store's `refetchPartner` only sets `isAuthorized: true` alongside a
    // non-null `partner` — so an authorized caller never passes `null` here. A
    // `null` partner means "not authenticated", which the auth guard already
    // redirected to `/login` before this function ran; allowing it through the
    // gate is a no-op, not a portal-access leak.
    return { kind: 'allow' }
  }

  // A newly granted admin is a staff "shell" partner with `can_access_portal`
  // false and no `terms_accepted_at`. Without this bypass they would land on the
  // beta-locked screen and never reach the admin dashboard.
  if (hasAdminRole(partner)) {
    return { kind: 'allow' }
  }

  if (!partner.can_access_portal) {
    return { kind: 'beta-locked' }
  }

  // Gate on a real timestamp, not just non-null. The field is an optional ISO
  // datetime, so "accepted" means a non-empty string. Treating `null`,
  // `undefined`, and `''` alike defends against a wire shape (absent field or
  // empty string) that would otherwise read as accepted and slip a partner in.
  if (partner.terms_accepted_at == null || partner.terms_accepted_at === '') {
    return { kind: 'terms' }
  }

  return { kind: 'allow' }
}
